import {
  DownloadOutlined,
  LeftOutlined,
  MenuOutlined,
  RightOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { MAX_SCALE, MIN_SCALE, SCALE_STEP } from './constants';

export interface IPreviewToolbarProps {
  scale: number;
  onScaleChange: (scale: number) => void;
  downloadUrl: string;
  fileName?: string;
  currentPage?: number;
  numPages?: number;
  onPageChange?: (page: number) => void;
  onToggleThumbnails?: () => void;
  onRotate?: () => void;
}

const roundScale = (value: number) => parseFloat(value.toFixed(2));

const PreviewToolbar: React.FC<IPreviewToolbarProps> = ({
  scale,
  onScaleChange,
  downloadUrl,
  fileName,
  currentPage = 1,
  numPages = 0,
  onPageChange,
  onToggleThumbnails,
  onRotate,
}) => (
  <Toolbar>
    <ToolbarGroup>
      {onToggleThumbnails && (
        <>
          <Tooltip title="Toggle thumbnails">
            <Button
              icon={<MenuOutlined />}
              type="text"
              onClick={onToggleThumbnails}
            />
          </Tooltip>
          <ToolbarDivider />
        </>
      )}
      {onPageChange && (
        <>
          <Tooltip title="Previous page">
            <Button
              icon={<LeftOutlined />}
              type="text"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            />
          </Tooltip>
          <PageInfo>
            {numPages > 0 ? `${currentPage} / ${numPages}` : '—'}
          </PageInfo>
          <Tooltip title="Next page">
            <Button
              icon={<RightOutlined />}
              type="text"
              disabled={currentPage >= numPages}
              onClick={() => onPageChange(currentPage + 1)}
            />
          </Tooltip>
        </>
      )}
    </ToolbarGroup>
    <ToolbarGroup>
      <Tooltip title="Zoom out">
        <Button
          icon={<ZoomOutOutlined />}
          type="text"
          disabled={scale <= MIN_SCALE}
          onClick={() =>
            onScaleChange(Math.max(MIN_SCALE, roundScale(scale - SCALE_STEP)))
          }
        />
      </Tooltip>
      <ZoomLabel>{Math.round(scale * 100)}%</ZoomLabel>
      <Tooltip title="Zoom in">
        <Button
          icon={<ZoomInOutlined />}
          type="text"
          disabled={scale >= MAX_SCALE}
          onClick={() =>
            onScaleChange(Math.min(MAX_SCALE, roundScale(scale + SCALE_STEP)))
          }
        />
      </Tooltip>
      {onRotate && (
        <>
          <ToolbarDivider />
          <Tooltip title="Rotate clockwise">
            <Button
              icon={<RotateRightOutlined />}
              type="text"
              onClick={onRotate}
            />
          </Tooltip>
        </>
      )}
      <ToolbarDivider />
      <Tooltip title="Download">
        <a
          href={downloadUrl}
          download={fileName}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button icon={<DownloadOutlined />} type="text" />
        </a>
      </Tooltip>
    </ToolbarGroup>
  </Toolbar>
);

export default PreviewToolbar;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.surface};
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 16px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 ${({ theme }) => theme.spacing.xs};
`;

const PageInfo = styled.span`
  font-family: ${({ theme }) => theme.typography.mono};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 56px;
  text-align: center;
  user-select: none;
`;

const ZoomLabel = styled.span`
  font-family: ${({ theme }) => theme.typography.mono};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 44px;
  text-align: center;
  user-select: none;
`;
